/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Tooltip, Modal, Upload, Select, DatePicker, Input, message, Spin, Popconfirm } from 'antd';
import { PlusOutlined, FilePdfOutlined, FileImageOutlined, FileOutlined, DeleteOutlined, EyeOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import FleetService from '../../services/FleetService';
import FileService from '../../services/FileService';
import moment from 'moment';

const { Option } = Select;

// ── Colour per doc type ───────────────────────────────────────────────────────
const DOC_COLORS = {
    'Blue Book':           '#1677ff',
    'Road Licence':        '#52c41a',
    'Insurance':           '#fa8c16',
    'Fitness Certificate': '#722ed1',
    'Inspection Report':   '#13c2c2',
    'Other':               '#8c8c8c',
};

// ── Expiry chip ───────────────────────────────────────────────────────────────
const ExpiryChip = ({ expiry }) => {
    if (!expiry) return null;
    const days = moment(expiry).diff(moment(), 'days');
    const color = days < 0 ? '#ff4d4f' : days < 30 ? '#fa8c16' : days < 90 ? '#fadb14' : '#52c41a';
    const label = days < 0 ? 'EXP' : days < 30 ? `${days}d` : moment(expiry).format('MMM YY');
    return (
        <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 4px',
            borderRadius: 3, background: color, color: days < 0 || days < 30 ? '#fff' : '#333',
            marginLeft: 2, verticalAlign: 'middle', letterSpacing: 0.3,
        }}>{label}</span>
    );
};

// ── File icon ─────────────────────────────────────────────────────────────────
const DocIcon = ({ ext }) => {
    if (!ext) return <FileOutlined />;
    const e = ext.toLowerCase().replace('.', '');
    if (e === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    if (['jpg','jpeg','png','webp'].includes(e)) return <FileImageOutlined style={{ color: '#1677ff' }} />;
    return <FileOutlined style={{ color: '#888' }} />;
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════════
export default function VehicleDocInline({ vehicleId, clientId, serialNo }) {
    const [docs, setDocs]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving]       = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [previewDoc, setPreviewDoc]     = useState(null); // doc to preview

    // form state
    const [docType, setDocType]         = useState(null);
    const [referenceNo, setReferenceNo] = useState('');
    const [issuer, setIssuer]           = useState('');
    const [issueDate, setIssueDate]     = useState(null);
    const [expiryDate, setExpiryDate]   = useState(null);
    const [notes, setNotes]             = useState('');

    const loaded = useRef(false);

    useEffect(() => {
        if (vehicleId && !loaded.current) { loaded.current = true; loadDocs(); }
    }, [vehicleId]);

    const loadDocs = async () => {
        setLoading(true);
        try {
            const r = await FleetService.getDocuments(vehicleId);
            if (r.data.code === 200) setDocs(r.data.result || []);
        } catch (e) {}
        setLoading(false);
    };

    const resetForm = () => {
        setDocType(null); setReferenceNo(''); setIssuer('');
        setIssueDate(null); setExpiryDate(null); setNotes('');
        setUploadedFile(null);
    };

    const handleUpload = async (info) => {
        const file = info.file.originFileObj || info.file;
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['pdf','jpg','jpeg','png','doc','docx'].includes(ext)) {
            message.error('Allowed: PDF, JPG, PNG, DOC, DOCX'); return;
        }
        if (file.size > 10 * 1024 * 1024) { message.error('Max 10MB'); return; }
        setUploading(true);
        try {
            const res = await FileService.upload(file, clientId);
            if (res.data.code === 200 && res.data.file_id) {
                setUploadedFile({
                    file_id:   res.data.file_id,
                    file_name: res.data.file?.file_name || file.name,
                    file_ext:  res.data.file?.file_ext || `.${ext}`,
                    file_url:  res.data.file_url || '',
                });
                message.success(`${file.name} ready`);
            } else message.error(res.data.message || 'Upload failed');
        } catch (e) { message.error('Upload failed'); }
        setUploading(false);
    };

    const saveDoc = async () => {
        if (!docType) { message.error('Select a document type'); return; }
        setSaving(true);
        try {
            const r = await FleetService.saveDocument({
                vehicle_id:        vehicleId,
                doc_type:          docType,
                issue_date:        issueDate?.format('YYYY-MM-DD') || null,
                expiry_date:       expiryDate?.format('YYYY-MM-DD') || null,
                issuing_authority: issuer,
                reference_no:      referenceNo,
                notes,
                file_id:           uploadedFile?.file_id || null,
            });
            if (r.data.code === 200) {
                message.success('Document saved');
                resetForm(); setModalOpen(false); loadDocs();
            } else message.error(r.data.message);
        } catch (e) { message.error('Save failed'); }
        setSaving(false);
    };

    const deleteDoc = async (id) => {
        try {
            const r = await FleetService.deleteDocument(id);
            if (r.data.code === 200) { loadDocs(); message.success('Removed'); }
        } catch (e) {}
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Inline pill strip ── */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 6, alignItems: 'center' }}>
                {loading && <Spin size="small" />}

                {docs.map(doc => {
                    const color = DOC_COLORS[doc.doc_type] || '#8c8c8c';
                    const expired = doc.status === 'Expired';
                    const expiring = doc.status === 'Expiring Soon';
                    return (
                        <Tooltip
                            key={doc.id}
                            title={
                                <div style={{ fontSize: 12 }}>
                                    <div><strong>{doc.doc_type}</strong></div>
                                    {doc.reference_no && <div>Ref: {doc.reference_no}</div>}
                                    {doc.issuing_authority && <div>By: {doc.issuing_authority}</div>}
                                    {doc.expiry_date && <div>Expires: {doc.expiry_date}</div>}
                                    {doc.file_id && <div style={{ color: '#90caf9', marginTop: 4 }}>📎 File attached</div>}
                                    <div style={{ marginTop: 6, color: '#aaa', fontSize: 11 }}>
                                        Click to preview · Right-click to delete
                                    </div>
                                </div>
                            }
                            placement="top"
                            overlayStyle={{ maxWidth: 220 }}
                        >
                            <span
                                onClick={() => setPreviewDoc(doc)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
                                    fontSize: 10, fontWeight: 600, lineHeight: 1.5,
                                    background: expired ? '#fff1f0' : expiring ? '#fff7e6' : `${color}15`,
                                    border: `1px solid ${expired ? '#ffccc7' : expiring ? '#ffe58f' : color}`,
                                    color: expired ? '#cf1322' : expiring ? '#d46b08' : color,
                                    transition: 'opacity 0.15s',
                                }}
                                onContextMenu={e => { e.preventDefault(); }}
                            >
                                {doc.file_id
                                    ? <DocIcon ext={doc.file_ext} />
                                    : <FileOutlined style={{ fontSize: 9 }} />
                                }
                                {doc.doc_type.split(' ').map(w => w[0]).join('')}
                                <ExpiryChip expiry={doc.expiry_date} />
                            </span>
                        </Tooltip>
                    );
                })}

                {/* ── Add button ── */}
                <Tooltip title="Add document (Blue Book, Insurance, Road Licence…)">
                    <span
                        onClick={() => { resetForm(); setModalOpen(true); }}
                        style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 20, height: 20, borderRadius: 4, cursor: 'pointer',
                            border: '1px dashed #d9d9d9', color: '#999', fontSize: 11,
                            background: '#fafafa',
                        }}
                    >
                        <PlusOutlined style={{ fontSize: 9 }} />
                    </span>
                </Tooltip>
            </div>

            {/* ── Preview modal ── */}
            {previewDoc && (
                <Modal
                    title={
                        <span style={{ color: DOC_COLORS[previewDoc.doc_type] || '#333' }}>
                            📄 {previewDoc.doc_type} — {serialNo}
                        </span>
                    }
                    open={!!previewDoc}
                    onCancel={() => setPreviewDoc(null)}
                    footer={null}
                    width={640}
                    zIndex={1200}
                    destroyOnClose
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginBottom: 16 }}>
                        {[
                            ['Document Type', previewDoc.doc_type],
                            ['Reference No',  previewDoc.reference_no || '—'],
                            ['Issued By',     previewDoc.issuing_authority || '—'],
                            ['Issue Date',    previewDoc.issue_date || '—'],
                            ['Expiry Date',   previewDoc.expiry_date
                                ? <span>{previewDoc.expiry_date} <ExpiryChip expiry={previewDoc.expiry_date} /></span>
                                : '—'
                            ],
                            ['Status',        <span style={{ color: previewDoc.status === 'Expired' ? '#ff4d4f' : previewDoc.status === 'Expiring Soon' ? '#fa8c16' : '#52c41a', fontWeight: 600 }}>{previewDoc.status || 'Valid'}</span>],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{label}</div>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{value}</div>
                            </div>
                        ))}
                    </div>

                    {previewDoc.notes && (
                        <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#555' }}>
                            {previewDoc.notes}
                        </div>
                    )}

                    {/* File view */}
                    {previewDoc.file_id && previewDoc.file_url ? (
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>📎 Attached File</div>
                            {/* PDF inline viewer */}
                            {(previewDoc.file_ext || '').toLowerCase().includes('pdf') ? (
                                <iframe
                                    src={previewDoc.file_url}
                                    style={{ width: '100%', height: 400, border: '1px solid #e0e0e0', borderRadius: 6 }}
                                    title="Document preview"
                                />
                            ) : ['jpg','jpeg','png','webp'].some(e => (previewDoc.file_ext||'').toLowerCase().includes(e)) ? (
                                <img
                                    src={previewDoc.file_url}
                                    alt="Document"
                                    style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 6, border: '1px solid #e0e0e0' }}
                                />
                            ) : (
                                <a href={previewDoc.file_url} target="_blank" rel="noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, color: '#1677ff', textDecoration: 'none', fontWeight: 500 }}>
                                    <DownloadOutlined /> Download {previewDoc.file_ext?.toUpperCase()} file
                                </a>
                            )}
                            <div style={{ marginTop: 8 }}>
                                <a href={previewDoc.file_url} target="_blank" rel="noreferrer"
                                    style={{ fontSize: 12, color: '#1677ff' }}>
                                    <EyeOutlined style={{ marginRight: 4 }} />Open in new tab
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#aaa', border: '1px dashed #e0e0e0', borderRadius: 6 }}>
                            No file attached to this document record
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                        <Popconfirm title="Remove this document?" onConfirm={() => { deleteDoc(previewDoc.id); setPreviewDoc(null); }} okText="Remove" okButtonProps={{ danger: true }}>
                            <span style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 13 }}>
                                <DeleteOutlined style={{ marginRight: 4 }} />Remove document
                            </span>
                        </Popconfirm>
                        <span style={{ color: '#999', fontSize: 12 }}>
                            Added by {previewDoc.created_by_name || 'unknown'} · {previewDoc.created ? moment(previewDoc.created).format('DD MMM YYYY') : ''}
                        </span>
                    </div>
                </Modal>
            )}

            {/* ── Add document modal ── */}
            <Modal
                title={`Add Document — ${serialNo}`}
                open={modalOpen}
                onCancel={() => { setModalOpen(false); resetForm(); }}
                onOk={saveDoc}
                okText={saving ? 'Saving…' : 'Save Document'}
                confirmLoading={saving}
                width={480}
                zIndex={1200}
                maskClosable={false}
                destroyOnClose
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {/* Type */}
                    <div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Document Type <span style={{ color: 'red' }}>*</span></div>
                        <Select style={{ width: '100%' }} value={docType} onChange={setDocType} placeholder="Select type">
                            <Option value="Blue Book">🔵 Blue Book (Vehicle Registration)</Option>
                            <Option value="Road Licence">🚗 Road Licence / Disc</Option>
                            <Option value="Insurance">🛡️ Insurance Certificate</Option>
                            <Option value="Fitness Certificate">✅ Fitness Certificate</Option>
                            <Option value="Inspection Report">🔍 Inspection Report</Option>
                            <Option value="Other">📄 Other</Option>
                        </Select>
                    </div>

                    {/* Ref + Issuer */}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Certificate / Reference No</div>
                            <Input value={referenceNo} onChange={e => setReferenceNo(e.target.value)} placeholder="e.g. INS-2024-001" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Issuing Authority</div>
                            <Input value={issuer} onChange={e => setIssuer(e.target.value)} placeholder="e.g. Road Transport Dept" />
                        </div>
                    </div>

                    {/* Dates */}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Issue Date</div>
                            <DatePicker style={{ width: '100%' }} value={issueDate} onChange={setIssueDate} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Expiry Date</div>
                            <DatePicker style={{ width: '100%' }} value={expiryDate} onChange={setExpiryDate} />
                        </div>
                    </div>

                    {/* Upload */}
                    <div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Upload File (PDF, JPG, PNG, DOC)</div>
                        <Upload beforeUpload={() => false} onChange={handleUpload} maxCount={1}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" showUploadList={false}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '6px 14px', border: '1px dashed #d9d9d9',
                                borderRadius: 6, cursor: 'pointer', fontSize: 13,
                                background: uploading ? '#f5f5f5' : '#fafafa',
                            }}>
                                <UploadOutlined />
                                {uploading ? 'Uploading…' : uploadedFile ? `✓ ${uploadedFile.file_name}` : 'Choose file'}
                            </span>
                        </Upload>
                        {uploadedFile && (
                            <div style={{ fontSize: 11, color: '#52c41a', marginTop: 4 }}>
                                ✓ {uploadedFile.file_name} ready to save
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Notes</div>
                        <Input.TextArea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" />
                    </div>

                </div>
            </Modal>
        </>
    );
}